import {
  ACTIONS,
  isActionName,
  type ActionName,
  type AgentCapability,
  type Permission,
  type ResourcePath,
  type Role,
  type ScopeType,
} from '@occ/contracts';

/**
 * RBAC del panel.
 *
 * Réplica exacta de `AuthorizationService` del backend, y a propósito: el panel
 * decide **qué pinta**, el backend decide **qué ocurre** (P2). Si esta copia se
 * equivocara de más, el usuario vería un botón que el backend le negará; si se
 * equivocara de menos, escondería algo que sí puede hacer. Ninguno de los dos
 * casos es un agujero de seguridad, porque la puerta sigue siendo el backend.
 */

const ROLE_RANK: Record<Role, number> = {
  read_only: 0,
  support_technician: 1,
  tenant_admin: 2,
};

export type Decision = { allowed: true } | { allowed: false; reason: string };

const ALLOWED: Decision = { allowed: true };

const resourceIdFor = (scope: ScopeType, resource: ResourcePath): string | undefined => {
  switch (scope) {
    case 'tenant':
      return resource.tenantId;
    case 'client':
      return resource.clientId;
    case 'branch':
      return resource.branchId;
    case 'node':
      return resource.nodeId;
  }
};

const covers = (permission: Permission, resource: ResourcePath): boolean => {
  const target = resourceIdFor(permission.scopeType, resource);
  return target !== undefined && target === permission.scopeId;
};

/** Mejor rol que el actor tiene sobre el recurso, o `null` si está fuera de su alcance. */
export const bestRoleFor = (permissions: Permission[], resource: ResourcePath): Role | null => {
  const granted = permissions.filter((permission) => covers(permission, resource));
  if (granted.length === 0) return null;
  return granted.reduce((best, current) =>
    ROLE_RANK[current.role] > ROLE_RANK[best.role] ? current : best,
  ).role;
};

export const hasRole = (
  permissions: Permission[],
  resource: ResourcePath,
  minRole: Role,
): boolean => {
  const best = bestRoleFor(permissions, resource);
  return best !== null && ROLE_RANK[best] >= ROLE_RANK[minRole];
};

export type Actorish = { tenantId: string; permissions: Permission[] };

export const authorize = (actor: Actorish, action: string, resource: ResourcePath): Decision => {
  if (!isActionName(action)) {
    return { allowed: false, reason: `Acción desconocida: ${action}` };
  }

  // Frontera de tenant primero: nada de otro inquilino es siquiera evaluable.
  if (actor.tenantId !== resource.tenantId) {
    return { allowed: false, reason: 'El recurso pertenece a otro tenant' };
  }

  const definition = ACTIONS[action as ActionName];
  const best = bestRoleFor(actor.permissions, resource);

  if (best === null) {
    return { allowed: false, reason: 'El recurso está fuera del alcance del usuario' };
  }
  if (ROLE_RANK[best] < ROLE_RANK[definition.minRole]) {
    return { allowed: false, reason: `Requiere el rol ${definition.minRole}` };
  }
  return ALLOWED;
};

/**
 * Capacidad que el agente debe declarar en su `HELLO` para que la acción tenga
 * sentido en ese nodo. Un agente viejo no soporta todo (ver `tunnel.ts`), así
 * que el panel apaga el botón en vez de dejar que el nodo responda `unsupported`.
 */
export const requiredCapability = (action: ActionName): AgentCapability | null => {
  if (action.startsWith('files.')) return 'files';
  if (action.startsWith('services.')) return 'services';
  if (action.startsWith('processes.')) return 'processes';
  if (action.startsWith('containers.')) return 'containers';
  switch (action) {
    case 'terminal.open':
      return 'terminal';
    case 'screen.open':
      return 'rustdesk';
    case 'script.run':
      return 'exec';
    case 'nodes.inventory':
      return 'inventory';
    default:
      return null;
  }
};
